<template>
    <div>
        <!-- Overdue Warning Block -->
        <div v-if="!loading && overdueServices && overdueServices.length" class="row mb-6">
            <div class="col">
                <div class="bg-red-100 border-l-4 border-red-500 text-red-700 px-4 py-3 sau-font sm" role="alert">
                    <p>
                        <span>The following services have been suspended due to unpaid invoices.</span>
                        <span>Reactivate services by paying all outstanding invoices.</span>
                    </p>
                    <p>
                        <div>
                            <span class="font-weight-bold">Suspended Services: </span>
                            <template v-for="(service, index) in services">
                                <span :key="index">
                                    <template v-if="index !== 0">, </template>
                                    <span>{{service.order_id}}</span>
                                </span>
                            </template>
                        </div>
                        <div>
                            <span class="font-weight-bold">Invoices: </span>
                            <template v-for="(invoice, index) in overdueInvoicesWithSuspensions">
                                <span :key="index">
                                    <template v-if="index !== 0">, </template>
                                    <a :href="`/billing/invoices/${invoice.id}`">#{{ invoice.id }}</a>
                                </span>
                            </template>
                        </div>
                    </p>
                    <div class="text-xs">* For immediate reactivation of suspended services, use an instant payment method (Credit Card or PayPal).</div>
                </div>
            </div>
        </div>

        <!-- Info Block -->
        <div class="row mb-6" v-if="!userFeatures.hasOwnProperty('generated-invoices')">
            <div class="col">
                <div class="d-flex align-items-center sau-bx">
                    <div class="p-2"><i class="fal fa-info-circle pl-2 sm primary"></i></div>
                    <div class="p-2">
                        <span>Your invoices are generated from your "Uninvoiced Items" and your invoice is generated 10 days from the date of payment.</span>
                    </div>
                </div>
                <a href="#" class="sau-font xs float-right sau-linx gray" @click.prevent="hideInfo('generated-invoices')">Don't show this again</a>
            </div>
        </div>

        <sau-template :retrieved="requestFinished">
            <!-- Fetching Placeholder -->
            <template slot="message">Fetching Invoices</template>
            <!-- Data -->
            <template slot="data">
                <table v-if="invoices.length > 0" id="data-table-invoices" class="table table-hover data-table sau-table" data-export="true" data-export-columns="[1, 2, 3, 4, 5, 6]" data-search="Search" cellspacing="0" width="100%" data-ordering="false">
                    <thead class="d-none">
                        <tr>
                            <th>Invoice</th>
                            <th class="noshow">Status</th>
                            <th class="noshow">Date Paid</th>
                            <th class="noshow">Invoice Number</th>
                            <th class="noshow">Invoice Date</th>
                            <th class="noshow">Due Date</th>
                            <th class="noshow">Amount</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="invoice in invoices" :key="invoice.id">
                            <td>

                                <a :href="`/billing/invoices/${invoice.id}`">
                                    <div class="d-flex product-list">
                                        <div>
                                            <i class="fal fa-file-invoice fa-2x py-2 pr-6 invoice-icon"></i>
                                        </div>
                                        <div>
                                            <p class="product-name">#{{ invoice.id }}</p>
                                            <p class="product-desc"><span class="sau-font-bold">Invoice Date:</span> {{ invoice.date | moment('DD/MM/YYYY') }}</p>
                                            <p class="product-desc"><span class="sau-font-bold">Due Date:</span> {{ invoice.duedate |  moment('DD/MM/YYYY') }}</p>
                                            <p class="product-desc"><span class="sau-font-bold">Amount:</span> {{ invoice.total | currency }}</p>
                                            <template v-if="invoice.status.toLowerCase() == 'paid'"><span class="d-none">{{ invoice.datepaid | moment('DD/MM/YYYY') }}</span></template>
                                            <p class="product-desc">
                                                <span class="sau-font-bold">Status:</span>
                                                <span :class="statusClass(invoice.status)">{{ invoice.status }}</span>
                                            </p>
                                        </div>
                                    </div>
                                </a>
                            </td>

                            <td :data-filter="applyFilter('status',invoice.status)" :data-order="invoice.status">
                                <!-- display date paid -->
                                <span v-if="invoice.status.toLowerCase() == 'paid'" class="sau-dot xs" v-b-tooltip.hover :title="createTooltip(invoice.datepaid)" :class="getStatus(invoice.status)"></span>
                                <span v-else class="sau-dot xs" v-b-tooltip.hover :title="invoice.status" :class="getStatus(invoice.status)"></span>
                                <template><span class="d-none">{{ invoice.status }}</span></template>
                            </td>

                            <td>
                                <template v-if="invoice.status.toLowerCase() == 'paid'"><span class="d-none">{{ invoice.datepaid | moment('DD/MM/YYYY') }}</span></template>
                                <template v-else><span class="d-none">NA</span></template>
                            </td>
                            <td :data-filter="applyFilter('invoice-number',invoice.id)"><i class="fal fa-file-invoice fa-2x py-2 pr-4" style="vertical-align: middle;"></i>{{ invoice.id }}</td>
                            <td :data-filter="applyFilter('invoice-date',invoice.date)" :data-order="convertDate(invoice.date)">{{ invoice.date | moment('DD/MM/YYYY') }}</td>
                            <td :data-filter="applyFilter('due-date',invoice.duedate)" :data-order="convertDate(invoice.duedate)">{{ invoice.duedate | moment('DD/MM/YYYY') }}</td>
                            <td class="text-right" :data-filter="applyFilter('balance',invoice.total)">{{ invoice.total | currency }}</td>
                        </tr>
                    </tbody>
                </table>
                <div v-else>

                    <sau-apiro-response type="message">
                        <template slot="message">You don't have any invoices at the moment.</template>
                    </sau-apiro-response>

                </div>
            </template>
        </sau-template>
        <sau-request-extension :invoice="requested"></sau-request-extension>
    </div>
</template>
<script>
import { mapState } from 'vuex';
import jsFunctions from '@js/store/jqueryFunctions';
export default {
    mixins: [jsFunctions],
    data() {
        return{
			invoices: [],
            features: {
                'pending-charges': true
            },
            requested: '',
            requestFinished: false,
            currentDate: moment(new Date).format('YYYY-MM-DD')
        }
    },
    created() {
        this.startApp();
        this.$store.dispatch('getOverdueServices');
    },
    computed:{
        ...mapState([
            'overdueServices',
            'loadingStates'
        ]),
        loading() {
            return this.loadingStates.overdueServices;
        },
        overdueOrderIds() {
            return this.overdueServices.map(service => service.order_id).join(', ');
        },
        services() {
            return this.overdueServices.map(service => {
                return {
                    ...service,
                    matchedProduct: this.getMatchedProduct(service.product),
                }
            });
        },
        overdueInvoicesWithSuspensions() {
            return this.overdueServices
                .flatMap(service => service.invoices)
                .reduce((unique, invoice) => {
                    return unique.some((u) => u.invoice_no === invoice.invoice_no) ? unique : [...unique, invoice];
                }, []);
        },
    },
    methods: {
        startApp() {
            this.getInvoices().then(response => {

                this.invoices = response.data.map(function(invoice) {
                    (invoice.status === 'Unpaid' && moment(this.currentDate).isAfter(invoice.duedate)) ? invoice.status = 'Overdue' : '';
                    return invoice;
                }, this);

                if(this.invoices) {
                    this.$nextTick(() => {
                        this.initializeDataTables();
                    });
                }
                this.requestFinished = true;
            }).catch(error => {
                this.requestFinished = true;
            });
        },
        applyFilter(column, data) {
            if(column == 'due-date' || column == 'invoice-date') {
                data = moment(data).format('DD/MM/YYYY')
            }
            return column+":"+data;
        },
        getStatus(status) {
            switch(status.toLowerCase()) {
                case 'paid' :
                    return 'success';
                        break;
                case 'unpaid' :
                    return 'unpaid';
                        break;
                case 'cancelled' :
                    return 'refunded';
                        break;
                case 'refunded' :
                    return 'refunded'
                case 'overdue' :
                    return 'overdue'
                default :
                    return 'unpaid';
                        break;
            }
        },
        validStatus(status) {
            switch(status.toLowerCase()) {
                case 'unpaid':
                case 'overdue':
                    return true;
                        break;
                default:
                    return false;
                        break;
            }
        },
        createTooltip(date) {
            /** Add date invoice was paid */
            return 'Paid ' + moment(date).fromNow();
        },
        setRequestedInvoice(invoice) {
            this.requested = invoice;
        },
        payInvoiceLink(invoice) {
            return '/billing/invoices/'+invoice;
        },
        rowLink(invoiceId){
            return window.location.href = '/billing/invoices/'+invoiceId;
        },
         statusClass(status) {
            switch(status.toLowerCase()) {

                case 'paid' :
                    return 'text-success';
                        break;
                case 'unpaid' :
                    return 'text-primary';
                        break;
                case 'cancelled' :
                    return 'text-grey';
                        break;
                case 'refunded' :
                    return 'text-grey';
                case 'overdue' :
                    return 'text-danger';
                case 'payment pending':
                    return 'text-primary';
                default :
                    return 'text-warning';
                        break;


            }
        },
    },

}
</script>
<style>
div.dataTables_wrapper div.dataTables_filter input {
    margin-top: -2px;
    padding: 10px 0 12px 60px;
}
#sau-dt-container .sau-menu-content {
	padding: 0px;
}
#sau-dt-buttons .dt-buttons button {
    width:100%;
    display:block;
    border:none;
}

#sau-dt-buttons .dt-buttons button:hover {
    color:#00ce7d !important;
    border:none;
}

#sau-dt-buttons .dt-buttons {
    margin-bottom: 0;
    border:none;
    padding:0 25px;
}
.sau-dot.refunded {
    background-color: #cccccc;
}
.sau-dot.warning {
    background-color: #ffdd58;
}
.sau-dot.overdue {
    background-color: #d8534f;
}
.sau-dot.unpaid {
    background-color: #0769ba;
}

.invoice-icon {
    color: #0769ba;
    font-size: 4.8rem;
}
.table a {
    color: #666;
    text-decoration: none;
}
</style>
